import { governanceService } from "@/services/governance.service";
import type { DefaultRoleTemplateItem, PermissionItem } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Edit2,
  Plus,
  Search,
  Shield,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import toast from "react-hot-toast";

// ─── Template Form Modal ────────────────────────────────────────────────────

interface TemplateFormModalProps {
  template?: DefaultRoleTemplateItem | null;
  onClose: () => void;
}

function TemplateFormModal({ template, onClose }: TemplateFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(template);

  const [roleName, setRoleName] = useState(template?.roleName ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [hierarchyLevel, setHierarchyLevel] = useState(
    template?.hierarchyLevel ?? 100,
  );
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set(template?.permissions ?? []),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { data: permissions } = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: () => governanceService.listPermissions(),
  });

  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, PermissionItem[]> = {};
    (permissions ?? []).forEach((perm) => {
      const group = perm.group || "General";
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(perm);
    });
    return grouped;
  }, [permissions]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedPermissions;

    const result: Record<string, PermissionItem[]> = {};
    Object.entries(groupedPermissions).forEach(([group, perms]) => {
      const filtered = perms.filter(
        (p) =>
          p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      if (filtered.length > 0) {
        result[group] = filtered;
      }
    });
    return result;
  }, [groupedPermissions, searchQuery]);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

  const togglePermission = (key: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllInGroup = (group: string) => {
    const perms = groupedPermissions[group] ?? [];
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      const allSelected = perms.every((p) => next.has(p.key));
      perms.forEach((p) => {
        allSelected ? next.delete(p.key) : next.add(p.key);
      });
      return next;
    });
  };

  const { mutateAsync: createTemplate, isPending: isCreating } = useMutation({
    mutationFn: (payload: {
      roleName: string;
      description?: string;
      hierarchyLevel?: number;
      permissions?: string[];
    }) => governanceService.createDefaultRoleTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-default-role-templates"],
      });
      toast.success("Template created successfully");
      onClose();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? "Failed to create template"),
  });

  const { mutateAsync: updateTemplate, isPending: isUpdating } = useMutation({
    mutationFn: (payload: {
      roleName?: string;
      description?: string;
      hierarchyLevel?: number;
      permissions?: string[];
    }) => governanceService.updateDefaultRoleTemplate(template!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-default-role-templates"],
      });
      toast.success("Template updated successfully");
      onClose();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? "Failed to update template"),
  });

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }

    const payload = {
      roleName: roleName.trim(),
      description: description.trim() || undefined,
      hierarchyLevel,
      permissions: Array.from(selectedPermissions),
    };

    if (isEditing) {
      await updateTemplate(payload);
    } else {
      await createTemplate(payload);
    }
  };

  const isSaving = isCreating || isUpdating;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm">
      <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-100 p-2">
              <Shield className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {isEditing ? "Edit Role Template" : "Create Role Template"}
              </h3>
              <p className="text-xs text-slate-500">
                Default permissions for new tenants
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
          {/* Role Name */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Role Name
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) =>
                setRoleName(e.target.value.toUpperCase().replace(/\s+/g, "_"))
              }
              placeholder="e.g. BUSINESS_OWNER"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this role"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Hierarchy Level */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Hierarchy Level{" "}
              <span className="font-normal normal-case text-slate-400">
                (lower = higher authority)
              </span>
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={hierarchyLevel}
              onChange={(e) =>
                setHierarchyLevel(parseInt(e.target.value, 10) || 100)
              }
              className="w-32 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Permissions */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Permissions ({selectedPermissions.size} selected)
            </label>

            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search permissions..."
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
              {Object.entries(filteredGroups).map(([group, perms]) => {
                const isExpanded = expandedGroups.has(group);
                const selectedCount = perms.filter((p) =>
                  selectedPermissions.has(p.key),
                ).length;
                const allSelected = selectedCount === perms.length;

                return (
                  <Fragment key={group}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                        )}
                        <span className="text-xs font-semibold text-slate-700">
                          {group}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                          {selectedCount}/{perms.length}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllInGroup(group);
                        }}
                        className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 hover:text-indigo-800"
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </button>
                    </button>
                    {isExpanded &&
                      perms.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex cursor-pointer items-center gap-3 px-3 py-1.5 pl-8 hover:bg-slate-50"
                        >
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded border ${
                              selectedPermissions.has(perm.key)
                                ? "border-indigo-600 bg-indigo-600"
                                : "border-slate-300"
                            }`}
                          >
                            {selectedPermissions.has(perm.key) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedPermissions.has(perm.key)}
                            onChange={() => togglePermission(perm.key)}
                            className="sr-only"
                          />
                          <span className="text-xs text-slate-700">
                            {perm.key}
                          </span>
                        </label>
                      ))}
                  </Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || !roleName.trim()}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving
              ? "Saving..."
              : isEditing
                ? "Update Template"
                : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

function DefaultRoleTemplatesPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DefaultRoleTemplateItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-default-role-templates"],
    queryFn: () => governanceService.listDefaultRoleTemplates(),
  });

  const { mutateAsync: deleteTemplate, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => governanceService.deleteDefaultRoleTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-default-role-templates"],
      });
      toast.success("Template deleted");
      setConfirmDelete(null);
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? "Failed to delete template"),
  });

  const { mutateAsync: toggleActive } = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      governanceService.updateDefaultRoleTemplate(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-default-role-templates"],
      });
      toast.success("Template status updated");
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message ?? "Failed to update template status",
      ),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
        <p className="mt-2 text-sm text-red-700">Failed to load templates</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Default Role Templates
            </h2>
            <p className="text-sm text-slate-500">
              Define which permissions new tenants get by default — no code
              release needed.
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setModalOpen(true);
            }}
            className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Manage default permissions for each role that new tenants receive.
            Changes here apply to <strong>future registrations</strong>. Use
            "Sync Permissions" on tenant detail pages to update existing
            tenants.
          </p>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setModalOpen(true);
            }}
            className="flex shrink-0 items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            New Template
          </button>
        </div>
      )}

      {(!templates || templates.length === 0) && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
          <Shield className="mx-auto h-12 w-12 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-600">
            No role templates defined
          </p>
          <p className="mt-1 text-xs text-slate-400">
            The system will fall back to code-defined defaults until you create
            templates.
          </p>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setModalOpen(true);
            }}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Create First Template
          </button>
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className={`rounded-2xl border p-5 transition-shadow hover:shadow-md ${
                tpl.isActive
                  ? "border-slate-200 bg-white"
                  : "border-slate-100 bg-slate-50 opacity-70"
              }`}
            >
              {/* Card header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`rounded-xl p-2 ${
                      tpl.isActive
                        ? "bg-indigo-100 text-indigo-600"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {tpl.roleName}
                    </h4>
                    {tpl.description && (
                      <p className="text-xs text-slate-500 capitalize">
                        {tpl.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      toggleActive({ id: tpl.id, isActive: !tpl.isActive })
                    }
                    title={
                      tpl.isActive ? "Disable template" : "Enable template"
                    }
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    {tpl.isActive ? (
                      <ToggleRight className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <ToggleLeft className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTemplate(tpl);
                      setModalOpen(true);
                    }}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(tpl.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1">
                  <span className="text-xs font-semibold text-slate-700">
                    {tpl.permissions.length}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    permissions
                  </span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1">
                  <span className="text-xs font-semibold text-slate-700">
                    L{tpl.hierarchyLevel}
                  </span>
                  <span className="text-[10px] text-slate-400">hierarchy</span>
                </div>
                <span
                  className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    tpl.isActive
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {tpl.isActive ? "Active" : "Disabled"}
                </span>
              </div>

              {/* Permission preview */}
              <div className="mt-3">
                <div className="flex flex-wrap gap-1">
                  {tpl.permissions.slice(0, 8).map((key) => (
                    <span
                      key={key}
                      className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
                    >
                      {key}
                    </span>
                  ))}
                  {tpl.permissions.length > 8 && (
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
                      +{tpl.permissions.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Delete Template?
              </h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              This will remove the default role template. Existing tenants will
              not be affected, but new tenant registrations will no longer
              include this role.
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteTemplate(confirmDelete)}
                disabled={isDeleting}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template form modal */}
      {modalOpen && (
        <TemplateFormModal
          template={selectedTemplate}
          onClose={() => {
            setModalOpen(false);
            setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
}

export default DefaultRoleTemplatesPage;
